import React, { useState } from "react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { SkeletonRows } from "../../Components/Common/LoadingStates";
import { KpiCard, MiniLineChart, Donut, fmt, shortDate } from "../../Components/Common/DashboardWidgets";
import { APIClient } from "../../helpers/api_helper";
import { GET_COINGECKO_USAGE } from "../../helpers/url_helper";
import { useCachedRequest } from "../../helpers/cachedFetch";

const api = new APIClient();

// Days each timeframe button requests from the backend.
const RANGES = [
  { label: "7d", days: 7 },
  { label: "14d", days: 14 },
  { label: "30d", days: 30 },
];

// Friendly names + colours for the endpoint namespaces the backend reports.
const ENDPOINT_LABELS = {
  markets: "Markets list",
  coins_list: "Coins list",
  chart_range: "Price charts",
  coin_details: "Coin details",
  contract: "Token metadata",
  onchain_multi: "Onchain prices",
  token_list: "Token lists",
  global: "Global market",
  search: "Search",
  prices: "Prices (/api/prices)",
};
const DONUT_COLORS = [
  "#5758FC", "#00CD52", "#FFB020", "#FF6B6B", "#22C7D6",
  "#B588F7", "#F78FB3", "#7ED957", "#FFD166", "#8AA0FF",
];

function labelFor(ep) {
  return ENDPOINT_LABELS[ep] || ep;
}

const th = {
  textAlign: "right", padding: "0.6rem 0.9rem", fontSize: 11, fontWeight: 600,
  color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.05em",
  borderBottom: "1px solid var(--bl-line)",
};
const thL = { ...th, textAlign: "left" };
const td = { padding: "0.6rem 0.9rem", textAlign: "right", fontSize: 13, fontVariantNumeric: "tabular-nums", borderBottom: "1px solid var(--bl-line)" };
const tdL = { ...td, textAlign: "left" };

const CoinGeckoUsage = () => {
  document.title = "CoinGecko API Usage | BiorGov Admin";
  const [range, setRange] = useState(RANGES[1]); // default 14d

  const req = useCachedRequest({
    key: `coingecko:usage:${range.days}`,
    fetcher: () => api.get(GET_COINGECKO_USAGE, { days: range.days }),
    deps: [range.days],
  });

  // successResponseWithData spreads fields at the top level; axios unwraps .data.
  const data = req.data || {};
  const summary = data.summary || {};
  const endpoints = Array.isArray(data.endpoints) ? data.endpoints : [];
  const daily = data.daily || { days: [], upstream: [], served: [] };

  const chartPoints = (daily.days || []).map((d, i) => ({
    x: i, y: (daily.upstream || [])[i] || 0, label: shortDate(d),
  }));

  // Donut: which endpoints burn the most CoinGecko quota (top 8 by upstream).
  const byUpstream = [...endpoints].filter((e) => e.upstream > 0).sort((a, b) => b.upstream - a.upstream);
  const donutSlices = byUpstream.slice(0, 8).map((e, i) => ({
    label: labelFor(e.endpoint), value: e.upstream, color: DONUT_COLORS[i % DONUT_COLORS.length],
  }));

  const priceRow = endpoints.find((e) => e.endpoint === "prices");
  const priceBreakdown = priceRow && priceRow.breakdown ? priceRow.breakdown : null;

  const savings = summary.savingsPct;
  const loading = req.loading;

  return (
    <div style={{ padding: "clamp(1rem, 2.5vw, 1.75rem)", maxWidth: 1400, margin: "0 auto" }}>
      <BreadCrumb title="CoinGecko API Usage" pageTitle="System" />

      {/* Timeframe + refresh */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: "1.25rem", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6 }}>
          {RANGES.map((r) => (
            <button
              key={r.label}
              onClick={() => setRange(r)}
              className="bl-glass"
              style={{
                padding: "0.35rem 0.9rem", fontSize: 13, borderRadius: 8, cursor: "pointer",
                border: range.days === r.days ? "1px solid var(--bl-accent)" : "1px solid var(--bl-line)",
                color: range.days === r.days ? "#fff" : "var(--bl-text-soft)",
                background: range.days === r.days ? "rgba(87,88,252,0.16)" : "transparent",
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
        {req.stale && <span style={{ fontSize: 12, color: "var(--bl-text-soft)" }}>refreshing…</span>}
        {req.error && !req.data && (
          <span style={{ fontSize: 12, color: "var(--bl-danger)" }}>Failed to load metrics</span>
        )}
        <button
          onClick={req.refetch}
          className="bl-glass"
          style={{ marginLeft: "auto", padding: "0.35rem 0.9rem", fontSize: 13, borderRadius: 8, cursor: "pointer", border: "1px solid var(--bl-line)", color: "var(--bl-text-soft)" }}
        >
          <i className="ri-refresh-line" style={{ marginRight: 6 }} />Refresh
        </button>
      </div>

      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
        <KpiCard
          label={`CoinGecko calls (${range.label})`}
          value={fmt(summary.coingeckoCallsTotal)}
          sub="Actual quota burn"
          icon="ri-arrow-up-circle-line"
          accent="var(--bl-accent)"
          loading={loading}
        />
        <KpiCard
          label="Requests served"
          value={fmt(summary.clientRequestsServed)}
          sub="From cache + upstream"
          icon="ri-server-line"
          accent="var(--bl-accent)"
          loading={loading}
        />
        <KpiCard
          label="Cache savings"
          value={savings == null ? "—" : `${savings}%`}
          sub="Requests answered without a CoinGecko call"
          icon="ri-shield-check-line"
          accent="var(--bl-success)"
          accentRgb="0,205,82"
          loading={loading}
        />
      </div>

      {/* Daily trend */}
      <div className="bl-glass" style={{ padding: "1.25rem", marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.75rem" }}>
          <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 600, color: "#fff" }}>CoinGecko calls per day</h3>
          <span style={{ fontSize: 12, color: "var(--bl-text-soft)" }}>lower is better</span>
        </div>
        {loading ? <div style={{ height: 140 }} /> : <MiniLineChart points={chartPoints} height={160} />}
      </div>

      {/* Per-endpoint table + donut */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.6fr) minmax(260px, 1fr)", gap: "1.5rem", alignItems: "start" }}>
        <div className="bl-glass" style={{ padding: "1.25rem", overflowX: "auto" }}>
          <h3 style={{ margin: "0 0 1rem 0", fontSize: "1rem", fontWeight: 600, color: "#fff" }}>Calls by endpoint</h3>
          {loading ? (
            <SkeletonRows rows={6} cols={5} />
          ) : endpoints.length === 0 ? (
            <p style={{ color: "var(--bl-text-soft)", fontSize: 13 }}>No usage recorded yet.</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
              <thead>
                <tr>
                  <th style={thL}>Endpoint</th>
                  <th style={th}>Requests</th>
                  <th style={th}>CoinGecko calls</th>
                  <th style={th}>Cache hit %</th>
                  <th style={th}>Errors</th>
                </tr>
              </thead>
              <tbody>
                {endpoints.map((e) => {
                  // A hit rate only means something where upstream calls are
                  // CAUSED by inbound requests. /api/prices upstream comes from
                  // the background refresh worker instead, so this ratio used to
                  // render as a nonsense negative (-122.9%). The backend now
                  // flags those rows; show "n/a" rather than a fake number.
                  const hitPct =
                    e.demandDriven === false || !e.served
                      ? null
                      : Math.round((1 - e.upstream / e.served) * 1000) / 10;
                  return (
                    <tr key={e.endpoint}>
                      <td style={tdL}>
                        <span style={{ color: "#fff" }}>{labelFor(e.endpoint)}</span>
                        <span style={{ color: "var(--bl-text-soft)", fontSize: 11, marginLeft: 6, fontFamily: "monospace" }}>{e.endpoint}</span>
                      </td>
                      <td style={td}>{fmt(e.served)}</td>
                      <td style={{ ...td, color: e.upstream > 0 ? "#fff" : "var(--bl-text-soft)" }}>{fmt(e.upstream)}</td>
                      <td style={{ ...td, color: hitPct >= 90 ? "var(--bl-success)" : hitPct >= 50 ? "#FFB020" : "var(--bl-text-soft)" }}>
                        {hitPct == null
                          ? (e.demandDriven === false ? (
                              <span title="Upstream calls here come from the background price-refresh worker, not from these requests, so a hit rate is undefined.">n/a</span>
                            ) : "—")
                          : `${hitPct}%`}
                      </td>
                      <td style={{ ...td, color: e.error > 0 ? "var(--bl-danger)" : "var(--bl-text-soft)" }}>{e.error || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {priceBreakdown && Object.keys(priceBreakdown).length > 0 && (
            <div style={{ marginTop: "1rem", paddingTop: "0.75rem", borderTop: "1px solid var(--bl-line)" }}>
              <div style={{ fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
                Price calls breakdown
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem 1.25rem" }}>
                {Object.entries(priceBreakdown).map(([k, v]) => (
                  <span key={k} style={{ fontSize: 12, color: "var(--bl-text-soft)" }}>
                    <span style={{ fontFamily: "monospace" }}>{k.replace("prices:", "")}</span>: <span style={{ color: "#fff" }}>{fmt(v)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bl-glass" style={{ padding: "1.25rem" }}>
          <h3 style={{ margin: "0 0 1rem 0", fontSize: "1rem", fontWeight: 600, color: "#fff" }}>Quota burn by endpoint</h3>
          {loading ? (
            <div style={{ height: 160 }} />
          ) : donutSlices.length === 0 ? (
            <p style={{ color: "var(--bl-text-soft)", fontSize: 13 }}>No CoinGecko calls in this window.</p>
          ) : (
            <Donut slices={donutSlices} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CoinGeckoUsage;
